import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Default: Solid Eucalyptus Sage with White text
        default: "bg-primary text-primary-foreground font-semibold shadow-md shadow-primary/20 hover:bg-sage-dark hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-2 border-primary/30 bg-transparent text-foreground hover:bg-primary/10 hover:border-primary/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm",
        ghost: "hover:bg-primary/10 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Botanical variants
        botanical: "bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/25 hover:bg-sage-dark hover:shadow-xl hover:shadow-primary/35 hover:-translate-y-0.5",
        sage: "bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25 hover:bg-sage-dark hover:shadow-xl hover:shadow-primary/35 hover:-translate-y-0.5",
        "botanical-outline": "border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground hover:shadow-lg",
        "botanical-ghost": "text-primary hover:bg-primary/10",
        // Legacy variants mapped to botanical
        luxury: "bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/25 hover:bg-sage-dark hover:shadow-xl hover:shadow-primary/35 hover:-translate-y-0.5",
        gold: "bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25 hover:bg-sage-dark hover:shadow-xl hover:shadow-primary/35 hover:-translate-y-0.5",
        "luxury-outline": "border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground hover:shadow-lg",
        "luxury-ghost": "text-primary hover:bg-primary/10",
        // Jungle variant for light backgrounds
        navy: "bg-jungle text-mint font-semibold shadow-md hover:bg-jungle-light hover:shadow-lg hover:-translate-y-0.5",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg font-semibold",
        icon: "h-10 w-10 aspect-square rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
